import { createApi } from '@reduxjs/toolkit/query/react';
import { createAuthBaseQuery } from './baseQuery';
import { API_BASE_URL } from '../constants/api';

// Voice of Partner — partner-facing RTK Query slice.
//
// Three endpoints mirroring backend `routes/feedback.js`:
//
//   • GET  /feedback/active        → current cycle + my submission
//                                     status (drives both the auto-
//                                     popup modal and the sidebar
//                                     "TrustLab Feedback" page).
//   • POST /feedback/responses     → submit my feedback for the
//                                     active cycle. 409 if I already
//                                     submitted; 403 if my user_type
//                                     isn't doctor / lab_partner.
//   • GET  /feedback/my-submission → my recorded answers (used by
//                                     the thank-you state to render
//                                     the recap tiles).
//
// Single tag 'PartnerFeedback' covers both the active cycle read
// and the my-submission read — submitting flips both, so the
// modal hides and the page swaps to the recap state without us
// having to invalidate them separately.
export const feedbackApi = createApi({
  reducerPath: 'feedbackApi',
  baseQuery: createAuthBaseQuery(API_BASE_URL),
  tagTypes: ['PartnerFeedback'],
  endpoints: (builder) => ({
    // GET /feedback/active. Returns:
    //   { cycle, hasSubmitted, mySubmission, eligible }
    // - cycle:        FeedbackCycle | null
    // - hasSubmitted: boolean
    // - mySubmission: FeedbackResponse | null
    // - eligible:     true only if the authed user_type is
    //                 doctor or lab_partner; false otherwise so
    //                 portal pages can hide the menu entry.
    getActiveFeedback: builder.query({
      query: () => '/feedback/active',
      providesTags: ['PartnerFeedback'],
    }),

    // POST /feedback/responses
    // Body shape: { csat: { dim: 1..5 }, nps: 0..10,
    //               ces: { stmt: 1..7 }, preferredLab, teamFeedback }
    submitFeedback: builder.mutation({
      query: (body) => ({
        url: '/feedback/responses',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PartnerFeedback'],
    }),

    // GET /feedback/my-submission — thank-you recap. Same response
    // shape as the inner `mySubmission` field of /active, so the
    // page can pull from either depending on which is freshest.
    getMySubmission: builder.query({
      query: () => '/feedback/my-submission',
      providesTags: ['PartnerFeedback'],
    }),
  }),
});

export const {
  useGetActiveFeedbackQuery,
  useSubmitFeedbackMutation,
  useGetMySubmissionQuery,
} = feedbackApi;
